import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  LinearProgress,
  Alert,
  CssBaseline,
  IconButton,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axios from "axios";
import CreditsBanner from "../Components/CreditsNudge";
import {
  ContentBox,
  StyledButton,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import YouTubeTagGeneratorContent from "../Contents/YtTagGeneratorContent.js";
import { Link } from "react-router-dom";
import { message } from "../Constants/message.js";
import CommentSection from "../Components/CommentSection.js";

function YouTubeTagGenerator({ user, login, isLoggedIn }) {
  const [keyword, setKeyword] = useState("");
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [credits, setCredits] = useState({
    remaining: 0,
    availed: 0,
    total: 0,
    loading: true,
  });

  useEffect(() => {
    const storedKeyword = localStorage.getItem("primaryInputText");
    if (storedKeyword) {
      setKeyword(storedKeyword);
      localStorage.removeItem("primaryInputText");
    }
  }, []);

  const handleKeywordChange = (e) => {
    setKeyword(e.target.value);
  };

  const handleGenerateTags = async () => {
    setSuccess("");
    setError("");
    if (!isLoggedIn) {
      setError(message.loginPrompt);
      setTimeout(() => {
        login();
      }, 1500);
      return;
    }
    if (!keyword.trim()) {
      setError("Please enter a keyword to generate tags.");
      return;
    }

    if (credits.remaining <= 0) {
      setError("Not enough credits to generate tags.");
      return;
    }

    setLoading(true);
    setTags([]);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/youtube/tagGenerator`,
        { tagInput: keyword },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );
      setLoading(false);
      setTags(response.data.tagInfo || []);
      setTimeout(() => setSuccess(response.data.successMessage), 200);

      // Fetch updated credits
      const creditsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/credit`
      );

      setCredits({
        remaining: creditsResponse.data.remaining_credits || 0,
        availed: creditsResponse.data.availed_credits || 0,
        total: creditsResponse.data.total_credits || 0,
        loading: false,
      });
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to generate tags. Please try again or contact support."
      );
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess("Tags copied to clipboard!");
  };

  return (
    <>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer>
        <ContentBox>
          <TitleText variant="h3" align="center">
            Generate YouTube Tags 🔖
          </TitleText>

          <SubtitleText variant="subtitle1" align="center">
            Enter a keyword to generate SEO-friendly YouTube tags
          </SubtitleText>

          <Box mb={3}>
            <TextField
              fullWidth
              label="Enter Keyword"
              variant="outlined"
              value={keyword}
              onChange={handleKeywordChange}
              placeholder="e.g. Travel Vlog, Tech Reviews, Cooking Tips"
              size="medium"
              id="youtube-tag-generator"
              name="primary-textbox"
            />
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box display="flex" justifyContent="center" gap={2}>
            <StyledButton
              variant="contained"
              color="success"
              onClick={handleGenerateTags}
            >
              Generate Tags
            </StyledButton>
          </Box>

          {tags.length > 0 && (
            <Box mt={3}>
              <Typography variant="body1" fontWeight="bold" mb={1}>
                Suggested YouTube Tags:
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  p: 2,
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  backgroundColor: "#f9f9f9",
                }}
              >
                {tags.map((tag, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{
                      backgroundColor: "#e0e0e0",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {tag}
                  </Typography>
                ))}
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => copyToClipboard(tags.join(", "))}
                >
                  <ContentCopyIcon />
                </IconButton>
              </Box>
            </Box>
          )}

          <Box mt={3}>
            <Typography variant="body2" color="text.secondary" align="center">
              <b>💡 1 Search keyword = 1 Credit</b>
              <br></br>
              Enter a keyword to generate relevant YouTube tags. Need help?{" "}
              <Link to="/contact" color="primary">
                Contact support
              </Link>
            </Typography>
          </Box>
        </ContentBox>
        <CommentSection
          toolId="youtube/tagGenerator"
          user={user}
          login={login}
          isLoggedIn={isLoggedIn}
        />
        <YouTubeTagGeneratorContent />
      </PageContainer>
    </>
  );
}

export default YouTubeTagGenerator;
